import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { asapScheduler, filter, Observable, observeOn, Subject, take, takeUntil, tap } from 'rxjs';

import { PageContextFacade, runtimeActions } from '@blaze-runtime/engine-data-access';
import {
  CanvasNamespaceId,
  DIALOG_OVERLAY_ID,
  type DialogClosePayload,
  DRAWER_OVERLAY_ID,
  getHTMLElementForNode,
} from '@blaze-runtime/engine-domain';
import { EngineDialogPages } from '@blaze-runtime/workflow-feature';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { PageContext } from '@blaze-shared/projects-domain';

import {
  addOverlayElement,
  layoutLoadingError,
  removeOverlayElement,
} from '../../+state/elements/elements.actions';
import { OVERLAY_SLOT_CLASS } from '../../elements/_overlay/_overlay.component';
import { OverlayElement } from '../../elements/_overlay/_overlay.element';
import { ElementDefinitionsService } from '../element-definitions.service';
import { LayoutLoadingService } from '../layout-loading.service';

export const MINIMUM_DISTANCE_TO_TOP = 30;

@Injectable({ providedIn: 'root' })
export class DialogOverlayService extends EngineDialogPages {
  private readonly actions$ = inject(Actions);
  private readonly elementDefinition = inject(ElementDefinitionsService);
  private readonly layoutLoadingService = inject(LayoutLoadingService);
  private readonly notificationService = inject(NotificationService);

  private readonly pageContextFacade = inject(PageContextFacade);
  private readonly store = inject(Store);

  private readonly dialogClose$ = new Subject<DialogClosePayload>();
  private currentDialogId?: string;
  private currentDialogOverlay?: OverlayElement;

  openDialog(
    dialogNamespaceId: CanvasNamespaceId,
    pageContext: PageContext | undefined,
    isDrawer = false,
  ): Observable<DialogClosePayload> {
    // Check if the dialog-like is already open
    // In case of a drawer, reuse if it's the same one
    if (this.currentDialogId && (!isDrawer || this.currentDialogId !== dialogNamespaceId)) {
      this.closeDialog(this.currentDialogId);
    }

    // Prepare a new dialog
    this.currentDialogId = dialogNamespaceId;
    if (!this.currentDialogOverlay) {
      this.currentDialogOverlay = this.prepareOverlayElement(dialogNamespaceId, isDrawer);
      this.store.dispatch(addOverlayElement({ overlayElement: this.currentDialogOverlay }));
    }

    this.loadDialogAndPassInput(dialogNamespaceId, pageContext, isDrawer);

    return this.dialogClose$.pipe(
      filter(({ dialogId }) => dialogId === dialogNamespaceId),
      take(1),
      tap(() => {
        this.currentDialogId = undefined;
        if (pageContext) {
          // Dialog-like overlay use page context based on pageId so they need to clear it to avoid conflicts when reopening
          this.pageContextFacade.removePageContext(pageContext.id);
        }
      }),
    );
  }

  closeDialog(dialogId: CanvasNamespaceId, payload?: any): void {
    this.currentDialogId = undefined;
    this.currentDialogOverlay = undefined;
    this.store.dispatch(removeOverlayElement({ hostedNs: dialogId }));
    this.dialogClose$.next({ dialogId, payload });
  }

  /**
   * The dialog overlay should open with its top edge close to the top of the viewport,
   * so the content is easily visible and accessible to thje user.
   * To achieve this, we need to calculate the top margin displacing the content of overlay within the page.
   * This method should be called when the dialog gets initialized.
   */
  adjustOverlayDisplacementOnInit(): void {
    // Reset the overlay displacement before calculating it (BL-8046)
    this.adjustOverlayDisplacement(0);

    // Calculate the top displacement of the dialog overlay to make it appear within the viewport
    const dialogOverlayElement = getHTMLElementForNode(DIALOG_OVERLAY_ID);
    if (!dialogOverlayElement) {
      return;
    }

    // Temporarily set max-height and overflow to calculate the top placement correctly (BL-8323)
    const previousMaxHeight = dialogOverlayElement.style.maxHeight;
    const previousOverflow = dialogOverlayElement.style.overflow;
    dialogOverlayElement.style.maxHeight = '0';
    dialogOverlayElement.style.overflow = 'hidden';

    const topPlacement = dialogOverlayElement.getBoundingClientRect().top;

    // Reset the styles to their previous values
    dialogOverlayElement.style.maxHeight = previousMaxHeight;
    dialogOverlayElement.style.overflow = previousOverflow;

    if (topPlacement < MINIMUM_DISTANCE_TO_TOP) {
      const topDisplacementPx = Math.abs(topPlacement - MINIMUM_DISTANCE_TO_TOP);
      this.adjustOverlayDisplacement(topDisplacementPx);
    }
  }

  closeCurrentDialog(dialogId?: string): void {
    if (!this.currentDialogId || (!!dialogId && this.currentDialogId !== dialogId)) {
      return;
    }

    this.closeDialog(this.currentDialogId);
  }

  private adjustOverlayDisplacement(topDisplacementPx: number): void {
    const overlayHostSlot = getHTMLElementForNode(DIALOG_OVERLAY_ID)?.querySelector(
      `.${OVERLAY_SLOT_CLASS}`,
    ) as HTMLElement | null;
    if (overlayHostSlot) {
      overlayHostSlot.style.marginTop = `${topDisplacementPx}px`;
    }
  }

  private loadDialogAndPassInput(
    dialogNamespaceId: string,
    pageContext: PageContext | undefined,
    isDrawer?: boolean,
  ): void {
    // Clear any previous output state for the dialog-like page, so the values from previous opening are not used
    this.store.dispatch(runtimeActions.clearDialogLikePageOutputs({ pageId: dialogNamespaceId }));
    if (pageContext) {
      this.pageContextFacade.setPageContextForOverlay(pageContext);
    }

    this.layoutLoadingService
      .loadAndWait(dialogNamespaceId)
      .pipe(
        observeOn(asapScheduler), // NOTE the dialog elements aren't in the elements collection immediately
        takeUntil(
          this.actions$.pipe(
            ofType(layoutLoadingError),
            filter(({ layout }) => layout.pageId === dialogNamespaceId),
            tap(({ err }) => {
              this.closeDialog(dialogNamespaceId);
              this.notificationService.showError(err, {
                customErrorMsg: 'Could not load the dialog',
              });
            }),
            take(1),
          ),
        ),
      )
      .subscribe(() => {
        this.store.dispatch(
          runtimeActions.autostartNamespace({
            ns: dialogNamespaceId,
            nsType: isDrawer ? 'Drawer' : 'Dialog',
          }),
        );
      });
  }

  private prepareOverlayElement(ns: CanvasNamespaceId, isDrawer: boolean): OverlayElement {
    const def = this.elementDefinition.getDefinitionSafe('_overlay');
    return {
      id: isDrawer ? DRAWER_OVERLAY_ID : DIALOG_OVERLAY_ID,
      ns: 'runtime',
      name: isDrawer ? 'Drawer Overlay' : 'Dialog Overlay',
      type: def.type,
      width: def.width,
      height: def.height,
      slots: {
        ids: ['main'],
        main: {
          children: [],
          hosting: { ns },
          sizing: { mode: 'fit-content' },
        },
      },
    };
  }
}
